import React, { StrictMode, useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import { api } from "./api";
import { AppShell, EmptyState, PageFrame, PrimaryButton } from "./layout";
import { Icon, MicrosoftMark } from "./icons";
import { pageRoutes } from "./pages/index.jsx";
import { ChatIAPage } from "./pages/chat-ia-page";
import "./app.css";
import "./utility.css";
import "./pages/orcamento.css";

const NAVIGATION_EVENT = "cfo-autus:navigate";
const VITE_BASE_PATH = import.meta.env.BASE_URL.replace(/\/$/, "");
// This asset belongs to the Vite package so the Docker build is fully
// reproducible; it preserves the existing Chevrolet login artwork.
const loginVehicle = `${import.meta.env.BASE_URL}login-car.webp`;

function isMountedBelowViteBase(pathname = window.location.pathname) {
  return Boolean(VITE_BASE_PATH) && (pathname === VITE_BASE_PATH || pathname.startsWith(`${VITE_BASE_PATH}/`));
}

function cleanPathname(pathname) {
  const appPathname = isMountedBelowViteBase(pathname) ? pathname.slice(VITE_BASE_PATH.length) || "/" : pathname;
  const value = appPathname.replace(/\/+$/, "") || "/dashboard";
  return value === "/" ? "/dashboard" : value;
}

function useBrowserPath() {
  const [pathname, setPathname] = useState(() => cleanPathname(window.location.pathname));
  useEffect(() => {
    const update = () => setPathname(cleanPathname(window.location.pathname));
    window.addEventListener("popstate", update);
    window.addEventListener(NAVIGATION_EVENT, update);
    return () => { window.removeEventListener("popstate", update); window.removeEventListener(NAVIGATION_EVENT, update); };
  }, []);
  return pathname;
}

/** Shared history navigation for the whole application; intentionally no router dependency. */
export function navigate(path, { replace = false } = {}) {
  const destination = cleanPathname(path);
  if (destination === cleanPathname(window.location.pathname)) return;
  // Vite serves the SPA below /app-assets in development; the production host
  // serves page routes at the root and only assets below that prefix.
  const browserDestination = isMountedBelowViteBase() ? `${VITE_BASE_PATH}${destination}` : destination;
  window.history[replace ? "replaceState" : "pushState"]({}, "", browserDestination);
  window.dispatchEvent(new Event(NAVIGATION_EVENT));
}

function matchesRoute(route, pathname) {
  const paths = Array.isArray(route.paths) ? route.paths : [route.path];
  return paths.filter(Boolean).some((path) => path === pathname || (route.nested && pathname.startsWith(`${path}/`)));
}

function UnknownPage() {
  return <PageFrame eyebrow="CFO Autus" title="Página não encontrada" description="O endereço acessado não está disponível nesta área."><section className="section-card"><EmptyState title="Escolha uma área no menu" description="Você pode voltar para a visão executiva a qualquer momento." action={<PrimaryButton onClick={() => navigate("/dashboard")}>Ir para a visão executiva</PrimaryButton>} /></section></PageFrame>;
}

function ChatIAAccessDenied() {
  return <PageFrame eyebrow="CFO.IA" title="Acesso restrito" description="Esta área está disponível apenas para administradores."><section className="section-card"><EmptyState title="Você não possui acesso ao CFO.IA" action={<PrimaryButton onClick={() => navigate("/dashboard")}>Voltar ao painel</PrimaryButton>} /></section></PageFrame>;
}

function RouteView({ pathname, user }) {
  const route = pageRoutes.find((candidate) => matchesRoute(candidate, pathname));
  if (route?.component === ChatIAPage && !user?.administrador) return <ChatIAAccessDenied />;
  const Component = route?.component || UnknownPage;
  return <Component user={user} />;
}

function LoginPage({ onAuthenticated }) {
  const [mode, setMode] = useState("loading");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.auth.config().then((config) => setMode(config?.sso ? "sso" : config?.login_local ? "local" : "unavailable")).catch(() => setMode("unavailable"));
  }, []);

  async function submitLocal(event) {
    event.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.auth.loginLocal(email);
      const user = await api.auth.me().catch(() => null);
      onAuthenticated(user);
      navigate("/dashboard", { replace: true });
    } catch (failure) {
      setError(failure.message || "Não foi possível entrar agora.");
    } finally {
      setSubmitting(false);
    }
  }

  return <div className="login-screen" style={{ "--login-car": `url(${loginVehicle})` }}>
    <section className="login-visual" aria-label="CFO Autus">
      <div className="login-brand"><span>CFO</span><strong>Autus</strong></div>
      <div className="login-visual-copy"><p>Inteligência financeira em movimento.</p></div>
      <span className="login-visual-mark" aria-hidden="true">01</span>
    </section>
    <main className="login-panel">
      <div className="login-card">
        <div className="login-card-brand"><span>CFO</span><strong>Autus</strong></div>
        <div className="login-heading"><span className="login-kicker">ACESSO SEGURO</span><h1>Bem-vindo.</h1><p>Entre com sua conta corporativa.</p></div>
        {mode === "loading" && <div className="login-loading"><i /> Carregando acesso</div>}
        {mode === "sso" && <a className="microsoft-button" href={api.auth.ssoLoginUrl}><MicrosoftMark />Entrar com Microsoft<Icon name="arrowUpRight" size={17} /></a>}
        {mode === "local" && <form className="login-form" onSubmit={submitLocal}>
          <label htmlFor="login-email">E-mail corporativo</label>
          <input id="login-email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" placeholder="nome@autus.com.br" required autoFocus />
          <button className="login-submit" type="submit" disabled={submitting}>{submitting ? "Entrando…" : "Entrar no CFO Autus"}<Icon name="arrowUpRight" size={17} /></button>
        </form>}
        {mode === "unavailable" && <div className="login-message login-warning">O acesso não está disponível neste ambiente.</div>}
        {error && <div className="login-message login-error" role="alert">{error}</div>}
        <p className="login-security"><span><Icon name="check" size={15} /></span> Ambiente corporativo protegido</p>
      </div>
    </main>
  </div>;
}

function Application() {
  const pathname = useBrowserPath();
  const isLogin = pathname === "/login";
  const [auth, setAuth] = useState({ status: isLogin ? "anonymous" : "checking", user: null });

  useEffect(() => {
    if (isLogin) return;
    let live = true;
    api.auth.me().then((user) => { if (live) setAuth({ status: "authenticated", user }); }).catch(() => {
      if (live) { setAuth({ status: "anonymous", user: null }); navigate("/login", { replace: true }); }
    });
    return () => { live = false; };
  }, [isLogin]);

  const handleAuthenticated = (user) => setAuth({ status: "authenticated", user });
  const handleLogout = async () => { try { await api.auth.logout(); } finally { setAuth({ status: "anonymous", user: null }); navigate("/login", { replace: true }); } };

  if (isLogin || auth.status === "anonymous") return <LoginPage onAuthenticated={handleAuthenticated} />;
  if (auth.status === "checking") return <div className="application-loading"><div><span className="brand-cfo">CFO</span><span className="brand-autus">Autus</span><i /></div></div>;
  return <AppShell pathname={pathname} navigate={navigate} user={auth.user} onLogout={handleLogout}><RouteView pathname={pathname} user={auth.user} /></AppShell>;
}

createRoot(document.getElementById("root")).render(<StrictMode><Application /></StrictMode>);
